import Backbone from "backbone";
import Parse from "parse";
import config from "../config";

const DEGRADED_MS = 60 * 1000;
const IGNORED_ERROR = "Additional properties not allowed";

let initialized = false;
let state = "connecting";
let downSince = null;
let outageObserved = false;
let degradedTimer = null;
let intentionalClose = false;

function debug(message, details) {
  if (!config.isDev) return;
  if (details === undefined) {
    console.debug(`[LiveQuery] ${message}`);
  } else {
    console.debug(`[LiveQuery] ${message}`, details);
  }
}

function clearDegradedTimer() {
  if (degradedTimer) {
    clearTimeout(degradedTimer);
    degradedTimer = null;
  }
}

function isIgnoredError(error) {
  const message =
    typeof error === "string" ? error : error?.message || error?.error || "";
  return message.includes(IGNORED_ERROR);
}

function markDown(error) {
  if (intentionalClose) return;

  // Arm the degraded timer once per outage. Parse retries at <30s intervals
  // and each failed attempt re-emits close/error, so resetting the timer on
  // every event would keep it from ever reaching DEGRADED_MS.
  if (!outageObserved) {
    outageObserved = true;
    downSince = Date.now();
    Backbone.trigger("livequery:closed", { error, downSince });
    degradedTimer = setTimeout(() => {
      degradedTimer = null;
      if (state !== "connected" && outageObserved && !intentionalClose) {
        Backbone.trigger("livequery:degraded", {
          error,
          downSince,
          elapsedMs: Date.now() - downSince,
        });
      }
    }, DEGRADED_MS);
  }

  state = "reconnecting";
  debug("connection lost; Parse will reconnect", error);
}

function handleOpen() {
  if (intentionalClose) {
    state = "disconnected";
    debug("ignored open during intentional shutdown");
    return;
  }

  const recovering = outageObserved && !intentionalClose;
  const elapsedMs = downSince ? Date.now() - downSince : 0;

  state = "connected";
  clearDegradedTimer();

  if (recovering) {
    debug(`transport reconnected after ${elapsedMs}ms`);
  } else {
    debug("connected");
  }

  Backbone.trigger("livequery:connected", { recovering, elapsedMs });
  outageObserved = false;
  downSince = null;
  intentionalClose = false;
}

function handleClose() {
  if (intentionalClose) {
    state = "disconnected";
    clearDegradedTimer();
    debug("closed intentionally");
    return;
  }
  markDown();
}

function handleError(error) {
  if (isIgnoredError(error)) {
    debug("ignored legacy schema warning", error);
    return;
  }
  markDown(error);
}

const LiveQueryMonitor = {
  init() {
    if (initialized) return;
    initialized = true;
    state = "connecting";
    Parse.LiveQuery.on("open", handleOpen);
    Parse.LiveQuery.on("close", handleClose);
    Parse.LiveQuery.on("error", handleError);
  },

  markIntentionalClose() {
    intentionalClose = true;
    state = "disconnected";
    outageObserved = false;
    downSince = null;
    clearDegradedTimer();
    Backbone.trigger("livequery:shutdown");
  },

  destroy() {
    if (initialized) {
      Parse.LiveQuery.removeListener("open", handleOpen);
      Parse.LiveQuery.removeListener("close", handleClose);
      Parse.LiveQuery.removeListener("error", handleError);
    }
    initialized = false;
    state = "connecting";
    downSince = null;
    outageObserved = false;
    intentionalClose = false;
    clearDegradedTimer();
  },

  getState() {
    return state;
  },
};

export { DEGRADED_MS };
export default LiveQueryMonitor;
